import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import apiClient, { getErrorMessage } from '@/api/apiClient';
import { t } from '@/i18n';

// OCR runs via the OCR.space free-tier API (see scan-receipt.php) - not client-side. Tesseract.js
// (in-browser OCR) was tried first, but its accuracy on real phone-photo receipts was too poor to
// be useful, and the server-side tesseract CLI that used to work well can't be installed on
// shared hosting (no root access). The photo is sent to our own backend, which forwards it to
// OCR.space and turns the recognized text into a suggested value/date/note/subcategory, the last
// of which is matched against this user's own past expenses (a database lookup only the server
// can do).
export function useReceiptScan() {
    const toast = useToast();
    const scanning = ref(false);

    const scanReceipt = async (file) => {
        scanning.value = true;

        try {
            const compressed = await compressReceiptImage(file);
            const formData = new FormData();
            formData.append('receipt', compressed, 'receipt.jpg');
            const response = await apiClient.post('/scan-receipt.php', formData);

            return {
                subcat: response.data.subcategoryId ?? '',
                value: response.data.value ?? '',
                note: response.data.note ?? '',
                // No date read off the receipt: today is the sane default (that's when most
                // people scan the receipt they just got), not an empty, unusable date field
                date: response.data.date ?? new Date().toISOString().slice(0, 10),
            };
        } catch (error) {
            console.error('Error scanning receipt:', error);
            toast.error(t('receipt.scanFailed', { detail: getErrorMessage(error) }));
            // Falls back to a blank-ish form (today's date only) instead of leaving the caller
            // with nothing: a failed scan is still a reason to open the form, not block the
            // user from adding the expense by hand
            return { subcat: '', value: '', note: '', date: new Date().toISOString().slice(0, 10) };
        } finally {
            scanning.value = false;
        }
    };

    return { scanning, scanReceipt };
}

// OCR.space's free tier caps uploads at 1MB - a phone camera photo is usually 2-5MB and gets
// silently rejected (no error, just an empty result) above that. Downscaling to a sane max
// dimension and re-encoding as JPEG at a reasonable quality keeps receipts (which are just text
// on paper, not detail-critical photos) comfortably under that limit while staying sharp enough
// to read. Drawing to a canvas also has the side effect of respecting the image's EXIF
// orientation (browsers auto-rotate <img>/canvas draws), so a sideways phone photo comes out
// upright too.
async function compressReceiptImage(file) {
    const MAX_DIMENSION = 1600;
    const QUALITY = 0.8;

    const bitmap = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob ?? file), 'image/jpeg', QUALITY);
    });
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (error) => {
            URL.revokeObjectURL(url);
            reject(error);
        };
        img.src = url;
    });
}
